import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { workedSaturdaysService, userService } from '../services/api';
import { Calendar, CheckCircle, XCircle, Save, RefreshCw, User } from 'lucide-react';
import toast from 'react-hot-toast';

const WorkedSaturdaysManagement = () => {
  const queryClient = useQueryClient();
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  // Buscar funcionários ativos
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users-for-saturdays'],
    queryFn: () => userService.getUsers({ limit: 100, active: 'true' }),
    staleTime: 5 * 60 * 1000,
  });

  const users = React.useMemo(() => {
    let list = [];
    if (!usersData) return [];
    if (Array.isArray(usersData?.data?.users)) list = usersData.data.users;
    else if (Array.isArray(usersData?.data?.data?.users)) list = usersData.data.data.users;
    else if (Array.isArray(usersData?.data)) list = usersData.data;
    
    // Filtrar apenas usuários ativos (is_active !== false) e ordenar por nome
    return list
      .filter(u => u.is_active !== false)
      .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
  }, [usersData]);

  // Buscar sábados trabalhados
  const { data: saturdaysData, isLoading, refetch } = useQuery({
    queryKey: ['worked-saturdays', selectedUserId, selectedYear, selectedMonth],
    queryFn: () =>
      selectedUserId
        ? workedSaturdaysService.getWorkedSaturdays({
            userId: selectedUserId,
            year: selectedYear,
            month: selectedMonth,
          })
        : Promise.resolve({ data: { data: [], meta: {} } }),
    enabled: !!selectedUserId,
    staleTime: 0,
  });

  const saturdays = React.useMemo(() => {
    if (!saturdaysData?.data?.data) return [];
    const result = Array.isArray(saturdaysData.data.data) ? saturdaysData.data.data : [];
    console.log('📅 Sábados carregados:', result);
    return result;
  }, [saturdaysData]);

  const meta = saturdaysData?.data?.meta || {};

  // Mutação para marcar/desmarcar sábado
  const toggleMutation = useMutation({
    mutationFn: ({ userId, date, worked, notes }) => {
      console.log('🔄 Toggle mutation chamada:', { userId, date, worked, notes });
      return workedSaturdaysService.toggleWorkedSaturday(userId, { date, worked, notes });
    },
    onMutate: async ({ userId, date, worked }) => {
      // Cancelar queries em andamento
      await queryClient.cancelQueries({ queryKey: ['worked-saturdays', userId, selectedYear, selectedMonth] });
      
      // Snapshot do valor anterior
      const previousData = queryClient.getQueryData(['worked-saturdays', userId, selectedYear, selectedMonth]);
      
      // Atualização otimista
      queryClient.setQueryData(['worked-saturdays', userId, selectedYear, selectedMonth], (old) => {
        if (!old?.data?.data) return old;
        const dateStr = date.split('T')[0];
        console.log('🔄 Atualização otimista:', { dateStr, worked, oldData: old.data.data });
        const updated = {
          ...old,
          data: {
            ...old.data,
            data: old.data.data.map(sat => {
              const satDateStr = sat.date.split('T')[0];
              if (satDateStr === dateStr) {
                console.log('✅ Atualizando sábado:', sat.date, '-> worked:', worked);
                return { ...sat, worked };
              }
              return sat;
            })
          }
        };
        console.log('📊 Dados atualizados:', updated.data.data);
        return updated;
      });
      
      return { previousData };
    },
    onSuccess: (response, variables, context) => {
      console.log('✅ Toggle mutation sucesso:', response);
      toast.success('Sábado atualizado com sucesso!');
    },
    onError: (error, variables, context) => {
      console.error('❌ Erro ao atualizar sábado:', error);
      // Reverter para o valor anterior em caso de erro
      if (context?.previousData) {
        queryClient.setQueryData(
          ['worked-saturdays', variables.userId, selectedYear, selectedMonth],
          context.previousData
        );
      }
      toast.error(error.response?.data?.message || 'Erro ao atualizar sábado');
    },
    onSettled: () => {
      // Sempre refetch após a mutação para garantir sincronização
      queryClient.invalidateQueries({ queryKey: ['worked-saturdays'] });
      queryClient.invalidateQueries({ queryKey: ['payslips'] });
    },
  });

  // Mutação para marcar múltiplos sábados
  const bulkToggleMutation = useMutation({
    mutationFn: ({ userId, saturdays }) =>
      workedSaturdaysService.bulkToggleWorkedSaturdays(userId, { saturdays }),
    onSuccess: () => {
      toast.success('Sábados atualizados com sucesso!');
      // Invalidar todas as queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['worked-saturdays'] });
      queryClient.invalidateQueries({ queryKey: ['payslips'] });
      // Forçar refetch da query atual com os parâmetros corretos
      queryClient.refetchQueries({ 
        queryKey: ['worked-saturdays', selectedUserId, selectedYear, selectedMonth] 
      });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar sábados');
    },
  });

  const monthNames = [
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro',
  ];

  const formatDate = (dateString) => {
    // Garantir que a data está no formato YYYY-MM-DD e criar data local (sem timezone)
    const dateStr = dateString.split('T')[0];
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day); // Criar data local (sem timezone)
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleToggle = (date, currentWorked) => {
    if (!selectedUserId) {
      toast.error('Selecione um funcionário primeiro');
      return;
    }

    // Garantir que a data está no formato YYYY-MM-DD (sem hora/timezone)
    const dateStr = date.split('T')[0]; // Pegar apenas a parte da data

    // Se está marcado como trabalhado, desmarcar
    // Se está marcado como não trabalhado, marcar como trabalhado
    // Se não está marcado, marcar como trabalhado
    const newWorked = currentWorked === true ? false : true;

    toggleMutation.mutate({
      userId: selectedUserId,
      date: dateStr, // Enviar apenas a data no formato YYYY-MM-DD
      worked: newWorked,
    });
  };

  const handleMarkAll = (worked) => {
    if (!selectedUserId) {
      toast.error('Selecione um funcionário primeiro');
      return;
    }

    const saturdaysToUpdate = saturdays
      .filter((sat) => sat.worked !== worked)
      .map((sat) => ({
        date: sat.date,
        worked: worked,
      }));

    if (saturdaysToUpdate.length === 0) {
      toast.info('Nenhum sábado para atualizar');
      return;
    }

    bulkToggleMutation.mutate({
      userId: selectedUserId,
      saturdays: saturdaysToUpdate,
    });
  };

  const selectedUser = users.find((u) => String(u.id) === String(selectedUserId));

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Controle de Sábados Trabalhados</h1>
          <p className="text-gray-600">
            Marque quais sábados cada funcionário trabalhou. Isso será usado no cálculo do holerite.
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
              <User className="w-4 h-4" />
              Funcionário
            </label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isLoadingUsers}
            >
              <option value="">
                {isLoadingUsers ? 'Carregando...' : 'Selecione um funcionário'}
              </option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name} ({u.role})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mês</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {monthNames.map((name, index) => (
                <option key={index + 1} value={index + 1}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ano</label>
            <input
              type="number"
              min="2020"
              max="2100"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => refetch()}
              disabled={isLoading || !selectedUserId}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
          </div>
        </div>
      </div>

      {/* Informação */}
      {selectedUser && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Como funciona:</strong> Marque os sábados que <strong>{selectedUser.full_name}</strong> trabalhou no
            mês de <strong>{monthNames[selectedMonth - 1]}/{selectedYear}</strong>. O valor fixo de sábado será
            aplicado apenas aos sábados marcados como trabalhados. Se nenhum sábado for marcado, o sistema usará todos
            os sábados do mês que têm jornada cadastrada.
          </p>
        </div>
      )}

      {/* Lista de Sábados */}
      {selectedUserId ? (
        isLoading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="text-gray-600 mt-4">Carregando sábados...</p>
          </div>
        ) : saturdays.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Nenhum sábado encontrado para este mês.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {/* Resumo */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Total de Sábados</p>
                <p className="text-2xl font-bold text-gray-900">{meta.totalSaturdays || 0}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-green-600">Trabalhados</p>
                <p className="text-2xl font-bold text-green-700">{meta.workedCount || 0}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4">
                <p className="text-sm text-red-600">Não Trabalhados</p>
                <p className="text-2xl font-bold text-red-700">{meta.notWorkedCount || 0}</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4">
                <p className="text-sm text-yellow-600">Não Marcados</p>
                <p className="text-2xl font-bold text-yellow-700">{meta.unmarkedCount || 0}</p>
              </div>
            </div>

            {/* Ações em lote */}
            <div className="mb-4 flex gap-2">
              <button
                onClick={() => handleMarkAll(true)}
                disabled={bulkToggleMutation.isLoading}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 text-sm flex items-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                Marcar Todos como Trabalhados
              </button>
              <button
                onClick={() => handleMarkAll(false)}
                disabled={bulkToggleMutation.isLoading}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 text-sm flex items-center gap-2"
              >
                <XCircle className="w-4 h-4" />
                Marcar Todos como Não Trabalhados
              </button>
            </div>

            {/* Lista de sábados */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {saturdays.map((saturday) => (
                <div
                  key={saturday.date}
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    saturday.worked === true
                      ? 'border-green-500 bg-green-50 hover:bg-green-100'
                      : saturday.worked === false
                      ? 'border-red-500 bg-red-50 hover:bg-red-100'
                      : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                  }`}
                  onClick={() => handleToggle(saturday.date, saturday.worked)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{formatDate(saturday.date)}</p>
                      <p className="text-xs text-gray-500">Dia {saturday.day} do mês</p>
                    </div>
                    <div>
                      {saturday.worked === true ? (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      ) : saturday.worked === false ? (
                        <XCircle className="w-6 h-6 text-red-600" />
                      ) : (
                        <div className="w-6 h-6 border-2 border-gray-400 border-dashed rounded-full"></div>
                      )}
                    </div>
                  </div>
                  {saturday.notes && (
                    <p className="text-xs text-gray-600 mt-2 italic">{saturday.notes}</p>
                  )}
                </div>
              ))}
            </div>

            {/* Legenda */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-2">Legenda:</p>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-gray-600">Trabalhado</span>
                </div>
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-600" />
                  <span className="text-gray-600">Não Trabalhado</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-gray-400 border-dashed rounded-full"></div>
                  <span className="text-gray-600">Não Marcado (usará padrão da jornada)</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Clique em um sábado para alternar entre trabalhado/não trabalhado.
              </p>
            </div>
          </div>
        )
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Selecione um funcionário para gerenciar os sábados trabalhados.</p>
        </div>
      )}
    </div>
  );
};

export default WorkedSaturdaysManagement;
